import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Wallet, FileText, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { paidByInvoice, invoiceOutstanding } from '../../lib/fees';
import { formatRM } from '../../lib/format';
import PageHeader from '../../components/PageHeader';
import { TableSkeleton } from '../../components/Skeletons';
import EmptyState from '../../components/EmptyState';
import StatusPill from '../../components/StatusPill';
import UserAvatar from '../../components/UserAvatar';
import ResitDialog from '../../components/ResitDialog';
import { StaggerList, StaggerItem } from '../../components/Motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { CenterMorphModal, CenterMorphModalContent } from '../../components/beui/CenterMorphModal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';

export default function StudentDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { school } = useAuth();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [kelas, setKelas] = useState('');
  const [bills, setBills] = useState([]);

  const [payTarget, setPayTarget] = useState(null);
  const [amount, setAmount] = useState('');
  const [kaedah, setKaedah] = useState('tunai');
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);
  const [resit, setResit] = useState(null);
  const [resitOpen, setResitOpen] = useState(false);

  const load = useCallback(async () => {
    const { data: st } = await supabase.from('students').select('id, nama, no_pelajar, class_id').eq('id', id).maybeSingle();
    if (!st) { setStudent(null); setLoading(false); return; }
    const [{ data: cls }, { data: invoices }, { data: payments }, { data: tariffs }] = await Promise.all([
      supabase.from('classes').select('nama').eq('id', st.class_id).maybeSingle(),
      supabase.from('invoices').select('*').eq('student_id', id).order('created_at'),
      supabase.from('payments').select('id, invoice_id, jumlah_rm, voided').eq('student_id', id),
      supabase.from('tariffs').select('id, nama'),
    ]);
    const paidMap = paidByInvoice(payments || []);
    const tMap = Object.fromEntries((tariffs || []).map((x) => [x.id, x.nama]));
    setStudent(st);
    setKelas(cls?.nama || '-');
    setBills((invoices || []).map((inv) => ({
      ...inv, tarifNama: tMap[inv.tariff_id] || 'Yuran',
      paid: paidMap[inv.id] || 0, baki: invoiceOutstanding(inv, paidMap),
    })));
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openPay = (bill) => { setPayTarget(bill); setAmount(String(bill.baki)); setKaedah('tunai'); setNota(''); };

  const confirmPay = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('Jumlah tidak sah.'); return; }
    setSaving(true);
    const { data, error } = await supabase.rpc('record_payment', {
      p_invoice: payTarget.id, p_amount: amt, p_kaedah: kaedah, p_nota: nota || null,
    });
    setSaving(false);
    if (error) { toast.error('Gagal merekod bayaran. Coba lagi.'); return; }
    toast.success(`Bayaran ${formatRM(amt)} direkod. Resit #${data.no_resit}`);
    setResit({
      no_resit: data.no_resit, tanggal: new Date().toISOString(), studentNama: student.nama,
      kelas, tarifNama: payTarget.tarifNama, amount: amt, kaedah, baki: data.baki,
    });
    setPayTarget(null);
    setResitOpen(true);
    load();
  };

  const totalOutstanding = bills.reduce((a, b) => a + b.baki, 0);

  return (
    <div>
      <PageHeader
        title={t('butiranPelajar')}
        breadcrumbs={[{ label: t('pengurusan') }, { label: t('pelajarKelas'), to: '/bendahara/pelajar' }, { label: student?.nama || '…' }]}
      />

      {loading ? <TableSkeleton /> : !student ? (
        <EmptyState icon={FileText} title={t('pelajarTidakDijumpai')} />
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-card p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <UserAvatar nama={student.nama} size={64} />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-foreground">{student.nama}</h2>
              <p className="text-sm text-muted-foreground">{kelas} · {student.no_pelajar}</p>
            </div>
            <div className="sm:text-right">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('jumlahTunggakan')}</p>
              <p className={`text-2xl sm:text-3xl font-black font-mono tabular ${totalOutstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald2'}`} data-testid="detail-outstanding">
                {formatRM(totalOutstanding)}
              </p>
            </div>
          </div>

          <h3 className="text-lg font-bold text-foreground mb-3">{t('bilYuran')}</h3>
          {bills.length === 0 ? (
            <EmptyState icon={Wallet} title={t('tiadaBil')} description="Bil akan dijana pada fasa penetapan yuran." />
          ) : (
            <StaggerList className="grid grid-cols-1 gap-3">
              {bills.map((b) => {
                const status = b.baki <= 0 ? 'lunas' : (b.paid > 0 ? 'separa' : 'belum');
                return (
                  <StaggerItem key={b.id} className="rounded-2xl border border-border bg-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:shadow-md transition-all" data-testid={`bill-${b.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{b.tarifNama}</p>
                        <StatusPill status={status} />
                      </div>
                      {b.diskon_nota && <p className="text-xs text-emerald2 mt-0.5">{b.diskon_nota}</p>}
                      <p className="text-xs text-muted-foreground mt-0.5">{t('dibayar')}: {formatRM(b.paid)} · {t('baki')}: {formatRM(b.baki)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl sm:text-2xl font-black font-mono tabular text-foreground">{formatRM(b.jumlah_bayar_rm)}</p>
                    </div>
                    <Button
                      onClick={() => openPay(b)}
                      disabled={b.baki <= 0}
                      className="h-12 px-6 text-base font-semibold bg-emerald2 hover:bg-emerald2-hover active:scale-[0.98] transition-transform disabled:opacity-40"
                      data-testid={`pay-btn-${b.id}`}
                    >
                      {b.baki <= 0 ? <><CheckCircle2 className="h-5 w-5 mr-1.5" />Lunas</> : <><Wallet className="h-5 w-5 mr-1.5" />{t('bayar')}</>}
                    </Button>
                  </StaggerItem>
                );
              })}
            </StaggerList>
          )}
        </>
      )}

      {/* Dialog konfirmasi bayaran — JUMLAH sangat besar */}
      <CenterMorphModal open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <CenterMorphModalContent ariaLabel={t('catatBayaran')} className="max-w-md bg-card" data-testid="pay-dialog">
          <div className="p-6">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{t('catatBayaran')}</h2>
            </div>
          {payTarget && (
            <div className="space-y-5">
              <div className="text-center rounded-2xl bg-brand-light p-5">
                <p className="text-sm text-muted-foreground">{payTarget.tarifNama} · {student?.nama}</p>
                <p className="mt-2 text-4xl sm:text-5xl font-black font-mono tabular text-brand" data-testid="pay-amount-display">
                  {formatRM(Number(amount) || 0)}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amt">{t('jumlahBayar')} (RM)</Label>
                <Input id="amt" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 text-lg font-mono" data-testid="pay-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>{t('kaedah')}</Label>
                <Select value={kaedah} onValueChange={setKaedah}>
                  <SelectTrigger className="h-12" data-testid="pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tunai">{t('tunai')}</SelectItem>
                    <SelectItem value="transfer">{t('transfer')}</SelectItem>
                    <SelectItem value="qr_manual">{t('qr_manual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nota">{t('notaP')}</Label>
                <Textarea id="nota" value={nota} onChange={(e) => setNota(e.target.value)} data-testid="pay-note" />
              </div>
              <Button onClick={confirmPay} disabled={saving} className="w-full h-14 text-lg font-bold bg-emerald2 hover:bg-emerald2-hover active:scale-[0.98] transition-transform" data-testid="pay-confirm">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t('sahkanBayaran')}
              </Button>
            </div>
          )}
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>

      <ResitDialog open={resitOpen} onOpenChange={setResitOpen} resit={resit} school={school} />
    </div>
  );
}
